import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiEndpoints } from '../utils/api';

interface Thread {
  id: string;
  _id?: string;
  title: string;
  running_jobs: any[];
  [key: string]: any;
}

interface ThreadsStore {
  // State
  threads: Thread[];
  currentThread: Thread | null;
  loading: boolean;           // For CRUD operations (create/update/delete/getThread)
  fetchingThreads: boolean;   // For fetching threads list only
  error: string | null;
  isPolling: boolean;
  pollIntervalId: NodeJS.Timeout | null;
  currentWorkspaceId: string | null;  // Track which workspace we're polling
  hasRunningJobs: boolean;
  agentStatus: 'generating' | 'stopped';
  
  // Actions
  setThreads: (threads: Thread[]) => void;
  setCurrentThread: (thread: Thread | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setAgentStatus: (status: 'generating' | 'stopped') => void;
  
  // Polling
  startPolling: (workspaceId: string, authenticatedApi: any) => void;
  stopPolling: () => void;
  restartPolling: (workspaceId: string, authenticatedApi: any) => void;
  fetchThreads: (workspaceId: string, authenticatedApi: any) => Promise<void>;
  updateHasRunningJobs: (threads: Thread[]) => void;
  
  // CRUD Operations
  getThread: (workspaceId: string, threadId: string, authenticatedApi: any) => Promise<Thread | null>;
  createThread: (workspaceId: string, threadData: any, authenticatedApi: any) => Promise<Thread | null>;
  updateThread: (workspaceId: string, threadId: string, updateData: any, authenticatedApi: any) => Promise<Thread | null>;
  deleteThread: (workspaceId: string, threadId: string, authenticatedApi: any) => Promise<boolean>;
}

export const useThreadsStore = create<ThreadsStore>()(
  devtools(
    (set, get) => {

      // Helper function to get thread ID
      const getThreadId = (thread: any): string => {
        return thread?.id || thread?._id || '';
      };

      const fetchThreadsInternal = async (workspaceId: string, authenticatedApi: any) => {
        if (!workspaceId || !authenticatedApi) {
          set({ error: 'Invalid parameters for fetching threads' });
          return;
        }

        try {
          const data = await authenticatedApi.get(apiEndpoints.threads(workspaceId));
          
          if (!Array.isArray(data)) {
            throw new Error('Invalid response format');
          }
          
          // Normalize threads to ensure id property
          const normalizedThreads = data.map((thread: any) => ({
            ...thread,
            id: thread.id || thread._id
          }));

          const hasRunningJobs = normalizedThreads.some((thread: Thread) => 
            thread.running_jobs && thread.running_jobs.length > 0
          );

          // Validate currentThread still exists in new threads array
          const state = get();
          const currentThreadId = state.currentThread ? getThreadId(state.currentThread) : null;
          const currentThreadStillExists = currentThreadId
            ? normalizedThreads.some((t: Thread) => getThreadId(t) === currentThreadId)
            : false;

          set({
            threads: normalizedThreads,
            error: null,
            hasRunningJobs,
            agentStatus: hasRunningJobs ? 'generating' : 'stopped',
            currentThread: currentThreadStillExists ? state.currentThread : null
          });
          
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch threads';
          set({ error: errorMessage });
          
          // Don't throw in polling context to avoid breaking the interval
          if (process.env.NODE_ENV === 'development') {
            console.error('Error fetching threads:', error);
          }
        }
      };

      return {
        // Initial state
        threads: [],
        currentThread: null,
        loading: false,
        fetchingThreads: false,
        error: null,
        isPolling: false,
        pollIntervalId: null,
        currentWorkspaceId: null,
        hasRunningJobs: false,
        agentStatus: 'stopped',

        // Actions
        setThreads: (threads) => {
          const hasRunningJobs = threads.some(thread => 
            thread.running_jobs && thread.running_jobs.length > 0
          );
          set({ 
            threads, 
            hasRunningJobs,
            agentStatus: hasRunningJobs ? 'generating' : 'stopped'
          });
        },

        setCurrentThread: (thread) => set({ currentThread: thread }),

        setLoading: (loading) => set({ loading }),
        
        setError: (error) => set({ error }),

        setAgentStatus: (status) => {
          set({ agentStatus: status });
        },

        updateHasRunningJobs: (threads) => {
          const hasRunningJobs = threads.some(thread =>
            thread.running_jobs && thread.running_jobs.length > 0
          );
          set({ 
            hasRunningJobs,
            agentStatus: hasRunningJobs ? 'generating' : 'stopped'
          });
        },

        fetchThreads: async (workspaceId, authenticatedApi) => {
          set({ fetchingThreads: true });
          await fetchThreadsInternal(workspaceId, authenticatedApi);
          set({ fetchingThreads: false });
        },

        startPolling: (workspaceId, authenticatedApi) => {
          const state = get();

          // Check if we're already polling this workspace
          if (state.isPolling && state.currentWorkspaceId === workspaceId) {
            // Already polling the same workspace, do nothing
            return;
          }

          // Stop existing polling if switching workspaces
          if (state.pollIntervalId) {
            clearInterval(state.pollIntervalId);
          }

          // Clear stale threads only if switching workspaces (or first time)
          const isSwitchingWorkspace = state.currentWorkspaceId && state.currentWorkspaceId !== workspaceId;
          if (isSwitchingWorkspace || !state.currentWorkspaceId) {
            set({ threads: [], fetchingThreads: true });
          }

          // Fetch immediately on start and clear loading flag when done
          fetchThreadsInternal(workspaceId, authenticatedApi).finally(() => {
            set({ fetchingThreads: false });
          });

          // Then set up interval for subsequent fetches
          const intervalId = setInterval(async () => {
            await fetchThreadsInternal(workspaceId, authenticatedApi);
          }, 5000);
            
          set({ 
            isPolling: true,
            currentWorkspaceId: workspaceId,
            pollIntervalId: intervalId
          });
        },

        restartPolling: (workspaceId, authenticatedApi) => {
          const state = get();
          
          // Stop current polling if active
          if (state.pollIntervalId) {
            clearInterval(state.pollIntervalId);
          }

          // Clear stale threads only if switching workspaces
          const isSwitchingWorkspace = state.currentWorkspaceId && state.currentWorkspaceId !== workspaceId;
          if (isSwitchingWorkspace) {
            set({ threads: [], fetchingThreads: true });
          }

          // Fetch immediately on restart and clear loading flag when done
          fetchThreadsInternal(workspaceId, authenticatedApi).finally(() => {
            set({ fetchingThreads: false });
          });

          // Then set up new polling interval
          const intervalId = setInterval(async () => {
            await fetchThreadsInternal(workspaceId, authenticatedApi);
          }, 5000);
          
          set({ 
            isPolling: true,
            currentWorkspaceId: workspaceId,
            pollIntervalId: intervalId
          });
        },

        stopPolling: () => {
          const state = get();
          
          if (state.pollIntervalId) {
            clearInterval(state.pollIntervalId);
            set({ 
              isPolling: false,
              pollIntervalId: null
            });
          }
        },

        // CRUD Operations
        getThread: async (workspaceId, threadId, authenticatedApi) => {
          if (!workspaceId || !threadId) {
            set({ error: 'Workspace ID and Thread ID are required' });
            return null;
          }

          set({ loading: true, error: null });

          try {
            const data = await authenticatedApi.get(`/workspaces/${workspaceId}/threads/${threadId}`);
            const normalizedThread = {
              ...data,
              id: getThreadId(data)
            };
            
            // Update both currentThread and ensure it's in the threads list
            const state = get();
            const updatedThreads = state.threads.map(thread =>
              getThreadId(thread) === threadId ? normalizedThread : thread
            );
            
            // If thread not in list, add it
            if (!updatedThreads.find(thread => getThreadId(thread) === threadId)) {
              updatedThreads.unshift(normalizedThread);
            }

            // Recalculate hasRunningJobs and agentStatus
            const hasRunningJobs = updatedThreads.some(thread =>
              thread.running_jobs && thread.running_jobs.length > 0
            );

            set({
              currentThread: normalizedThread,
              threads: updatedThreads,
              hasRunningJobs,
              agentStatus: hasRunningJobs ? 'generating' : 'stopped',
              loading: false
            });
            return normalizedThread;
          } catch (error) {
            console.error('Error fetching thread:', error);
            set({ error: error instanceof Error ? error.message : 'Failed to fetch thread', loading: false });
            return null;
          }
        },

        createThread: async (workspaceId, threadData, authenticatedApi) => {
          if (!workspaceId) {
            set({ error: 'Workspace ID is required' });
            return null;
          }

          if (!threadData || !threadData.title) {
            set({ error: 'Thread title is required' });
            return null;
          }

          set({ loading: true, error: null });

          try {
            const data = await authenticatedApi.post(apiEndpoints.threads(workspaceId), threadData);
            const normalizedThread = {
              ...data,
              id: getThreadId(data)
            };


            // Update current thread and add to threads list
            const currentThreads = get().threads;
            set({ 
              currentThread: normalizedThread,
              threads: [normalizedThread, ...currentThreads],
              loading: false 
            });
            
            return normalizedThread;
          } catch (error) {
            console.error('Error creating thread:', error);
            set({ error: error instanceof Error ? error.message : 'Failed to create thread', loading: false });
            return null;
          }
        },

        updateThread: async (workspaceId, threadId, updateData, authenticatedApi) => {
          if (!workspaceId || !threadId) {
            set({ error: 'Workspace ID and Thread ID are required' });
            return null;
          }

          if (!updateData || Object.keys(updateData).length === 0) {
            set({ error: 'Update data is required' });
            return null;
          }

          set({ loading: true, error: null });

          try {
            const data = await authenticatedApi.put(`/workspaces/${workspaceId}/threads/${threadId}`, updateData);
            const normalizedThread = {
              ...data,
              id: getThreadId(data)
            };

            // Update current thread if it's the one being edited
            const state = get();
            const updatedCurrentThread = state.currentThread && getThreadId(state.currentThread) === threadId 
              ? normalizedThread 
              : state.currentThread;

            // Update threads list
            const updatedThreads = state.threads.map(thread =>
              getThreadId(thread) === threadId ? normalizedThread : thread
            );

            // Recalculate hasRunningJobs and agentStatus
            const hasRunningJobs = updatedThreads.some(thread =>
              thread.running_jobs && thread.running_jobs.length > 0
            );

            set({
              currentThread: updatedCurrentThread,
              threads: updatedThreads,
              hasRunningJobs,
              agentStatus: hasRunningJobs ? 'generating' : 'stopped',
              loading: false
            });

            return normalizedThread;
          } catch (error) {
            console.error('Error updating thread:', error);
            set({ error: error instanceof Error ? error.message : 'Failed to update thread', loading: false });
            return null;
          }
        },

        deleteThread: async (workspaceId, threadId, authenticatedApi) => {
          if (!workspaceId || !threadId) {
            set({ error: 'Workspace ID and Thread ID are required' });
            return false;
          }

          set({ loading: true, error: null });

          try {
            await authenticatedApi.delete(`/workspaces/${workspaceId}/threads/${threadId}`);

            // Update state
            const state = get();
            const updatedCurrentThread = state.currentThread && getThreadId(state.currentThread) === threadId 
              ? null 
              : state.currentThread;

            const updatedThreads = state.threads.filter(thread => getThreadId(thread) !== threadId);

            set({ 
              currentThread: updatedCurrentThread,
              threads: updatedThreads,
              loading: false 
            });

            return true;
          } catch (error) {
            console.error('Error deleting thread:', error);
            set({ error: error instanceof Error ? error.message : 'Failed to delete thread', loading: false });
            return false;
          }
        },
      };
    },
    {
      name: 'threads-store',
    }
  )
);

// Essential selector hooks
export const useThreads = () => useThreadsStore((state) => state.threads);
export const useCurrentThread = () => useThreadsStore((state) => state.currentThread);
export const useThreadsLoading = () => useThreadsStore((state) => state.loading);           // CRUD operations loading
export const useThreadsFetching = () => useThreadsStore((state) => state.fetchingThreads); // List fetching loading
export const useThreadsError = () => useThreadsStore((state) => state.error);
export const useHasRunningJobs = () => useThreadsStore((state) => state.hasRunningJobs);
export const useAgentStatus = () => useThreadsStore((state) => state.agentStatus);

// Action hooks
export const useThreadActions = () => useThreadsStore((state) => ({
  getThread: state.getThread,
  createThread: state.createThread,
  updateThread: state.updateThread,
  deleteThread: state.deleteThread,
  setCurrentThread: state.setCurrentThread,
}));

export const useThreadsPolling = () => useThreadsStore((state) => ({
  isPolling: state.isPolling,
  startPolling: state.startPolling,
  stopPolling: state.stopPolling,
  restartPolling: state.restartPolling,
}));

// Development helper
if (process.env.NODE_ENV === 'development') {
  (window as any).threadsStore = useThreadsStore;
  (window as any).getThreadsInfo = () => useThreadsStore.getState();
}
